import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

/**
 * Fil d'Ariane (breadcrumb) auto-généré depuis l'URL.
 *
 * Aligné sur la sidebar : Cabinet · Fiscal · Entrepreneurs · Immeubles · Files PMS ·
 * Centre de communications · Paramètres · Labo.
 * Le Dashboard est accessible via le crumb « Accueil » (/admin).
 *
 * Usage standard : `<Breadcrumb />` (rendu globalement dans MainLayout)
 * Usage avancé   : pages détail injectent un trail via BreadcrumbContext.
 */
const LABEL_MAP = {
  // Racines / Accueil
  admin: "Accueil",
  dashboard: "Opérations",

  // Hubs sidebar (9 entrées)
  cabinet: "Cabinet",
  fiscal: "Fiscal",
  autonome: "Tenue de livres",
  entrepreneurs: "Tenue de livres",
  paie: "Paie",
  "tenue-livres": "Tenue de livres",
  immeubles: "Immeubles",
  "pms-queue": "Files PMS",
  documents: "Centre de communications",
  settings: "Paramètres",
  test: "Labo (idées)",

  // Sous-modules Cabinet
  masters: "Master",
  "cabinet-taxes": "Taxes TPS/TVQ",
  aide: "Mode d'emploi et aide",
  "ccq-mode-emploi": "Mode d'emploi — Paies CCQ",
  "ccq-feuillet": "Feuillet explicatif — CCQ",
  comptabilite: "Comptabilité",
  "home-office-calculator": "Calculatrice bureau à domicile",
  acomptes: "Mes acomptes provisionnels",
  conformite: "Calendrier de conformité",
  rentabilite: "Rentabilité par dossier",
  reports: "Rapports",
  bureau: "Bureau",
  appointments: "Rendez-vous",
  tasks: "Tâches & check-listes",
  timer: "Timer",

  // Sous-modules Fiscal
  "fiscal-dossiers": "Dossiers Fiscaux",
  "tax-lines": "Lignes fiscales",
  "tax-rates": "Taux d'imposition",
  "base-fiscale": "Expertise Fiscale",

  // Sous-modules Immeubles
  "immeuble-dossiers": "Dossiers Immeubles",
  "base-immeuble": "Expertise Immeubles",
  "bail-workflow": "Workflow bail",
  "payment-success": "Paiement confirmé",
  "payment-cancelled": "Paiement annulé",

  // Sous-modules Entrepreneurs (Batch #14.243)
  apercu: "Aperçu",
  revenus: "Revenus",
  depenses: "Dépenses",
  kilometrage: "Kilométrage",
  taxes: "TPS / TVQ",

  // Sous-modules Paie (Batch #14.250)
  employes: "Employés",
  periodes: "Périodes de paie",
  bulletins: "Bulletins",
  remises: "Remises DAS",
  ccq: "CCQ",
  "fin-annee": "Fin d'année",
  "releves-emploi": "Relevés d'emploi",
  reglages: "Réglages",

  // Sous-modules Centre de communications
  "pdf-templates": "Modèles PDF",
  "client-documents": "Documents à signer",
  "communications-hub": "Suivi comms",
  messages: "Messages",
  questions: "Chat clients",
  sent: "Envois",
  received: "Dépôts",
  communications: "Suivi des communications",
  inbox: "Inbox",
  "sms-inbox": "Boîte SMS",

  // Sous-modules Portail
  "portail-clients": "Portail clients",
  "portal-admin": "Configurer compte portail",
  "diagnostic-pastilles": "Diagnostic pastilles",

  // Files PMS
  "t777-submissions": "Bureau à domicile (T777)",

  // Paramètres + sections
  smtp: "Serveur SMTP",
  invoice: "Facturation",
  stripe: "Stripe",
  backup: "Sauvegarde",
  integrations: "Intégrations",
  account: "Compte",
  security: "Sécurité",
  audit: "Piste d'audit",
  "archive-bin": "Archivage",
  "clients-trash": "Corbeille",

  // Activité / divers
  notifications: "Notifications",
  leads: "Leads",
  clients: "Clients",
};

// Mapping pour les valeurs de query params (?tab=account → "Compte")
const QUERY_LABEL_MAP = {
  account: "Compte",
  cabinet: "Cabinet",
  invoice: "Facturation",
  smtp: "Serveur SMTP",
  stripe: "Configuration Stripe",
  notifications: "Paramètres de notifications",
  sms: "SMS",
  portal: "Portail",
  backup: "Sauvegarde",
  integrations: "Mes intégrations",
  legal: "Pages légales",
  expenses: "Dépenses",
  receipts: "Reçus",
  categories: "Catégories",
  comptes: "Comptes clients",
  acces: "Accès aux tuiles",
  modeles: "Modèles PDF",
  // Files PMS (Batch #14.314 — section Entreprise + onglets dans l'URL)
  credit: "Enquêtes de crédit", rl31: "Demandes RL-31", leases: "Baux récents", rent_increase: "Avis de renouvellement", payments: "Paiements Stripe",
  revenues: "Revenus en attente", docs_expenses: "Documents & Dépenses", t777: "Bureau à domicile (T777)", mileage: "Kilométrages immeuble", work_orders: "Suivi des travaux",
  ent_revenues: "Entreprise · Factures de revenus", ent_expenses: "Entreprise · Dépenses & reçus", ent_mileage: "Entreprise · Kilométrage",
  // Comptabilité (Batch #14.265 · #14.273)
  plan: "Plan comptable",
  ecritures: "Journal général",
  "grand-livre": "Grand livre",
  rapports: "Rapports",
  fournisseurs: "Fournisseurs",
  "transactions-clients": "Transactions clients",
  "transactions-fournisseurs": "Transactions fournisseurs",
  produits: "Produits & services",
};

/**
 * Mapping parent-launcher pour chaque sous-module.
 *
 * Aligné sur la sidebar.
 * Quand on est sur un sous-module accessible via un Hub, on insère
 * automatiquement le Hub dans le fil d'Ariane :
 *   /admin/invoices  →  Accueil › Cabinet › Factures
 */
const PARENT_LAUNCHER_MAP = {
  // ── Cabinet ──────────────────────────────────────────────────────
  "masters": { label: "Cabinet", href: "/admin/cabinet" },
  "cabinet-taxes": { label: "Cabinet", href: "/admin/cabinet" },
  "aide": { label: "Cabinet", href: "/admin/cabinet" },
  "comptabilite": { label: "Cabinet", href: "/admin/cabinet" },
  "home-office-calculator": { label: "Cabinet", href: "/admin/cabinet" },
  "acomptes": { label: "Cabinet", href: "/admin/cabinet" },
  "conformite": { label: "Cabinet", href: "/admin/cabinet" },
  "rentabilite": { label: "Cabinet", href: "/admin/cabinet" },
  "reports": { label: "Cabinet", href: "/admin/cabinet" },
  "bureau": { label: "Cabinet", href: "/admin/cabinet" },
  "appointments": { label: "Cabinet", href: "/admin/cabinet" },
  "tasks": { label: "Cabinet", href: "/admin/cabinet" },
  "timer": { label: "Cabinet", href: "/admin/cabinet" },
  "leads": { label: "Cabinet", href: "/admin/cabinet" },

  // ── Fiscal ───────────────────────────────────────────────────────
  "fiscal-dossiers": { label: "Fiscal", href: "/admin/fiscal" },
  "tax-lines": { label: "Fiscal", href: "/admin/fiscal" },
  "tax-rates": { label: "Fiscal", href: "/admin/fiscal" },
  "base-fiscale": { label: "Fiscal", href: "/admin/fiscal" },

  // ── Immeubles ────────────────────────────────────────────────────
  "immeuble-dossiers": { label: "Immeubles", href: "/admin/immeubles" },
  "base-immeuble": { label: "Immeubles", href: "/admin/immeubles" },
  "bail-workflow": { label: "Immeubles", href: "/admin/immeubles" },

  // ── Files PMS ────────────────────────────────────────────────────
  "t777-submissions": { label: "Files PMS", href: "/admin/pms-queue" },

  // ── Centre de communications ─────────────────────────────────────
  "communications-hub": { label: "Centre de communications", href: "/admin/documents" },
  "client-documents": { label: "Centre de communications", href: "/admin/documents" },
  "messages": { label: "Centre de communications", href: "/admin/documents" },
  "pdf-templates": { label: "Centre de communications", href: "/admin/documents" },
  "communications": { label: "Centre de communications", href: "/admin/documents" },
  "inbox": { label: "Centre de communications", href: "/admin/documents" },
  "sms-inbox": { label: "Centre de communications", href: "/admin/documents" },

  // ── Portail clients (tuile Cabinet) ─────────────────────────────
  "portail-clients": { label: "Cabinet", href: "/admin/cabinet" },
  "portal-admin": { label: "Portail clients", href: "/admin/portail-clients" },

  // ── Paramètres (tuiles accessibles depuis la page Settings) ──────
  "security": { label: "Paramètres", href: "/admin/settings" },
  "audit": { label: "Paramètres", href: "/admin/settings" },
  "archive-bin": { label: "Paramètres", href: "/admin/settings" },
  "clients-trash": { label: "Paramètres", href: "/admin/settings" },

  // ── Messagerie (sous-niveau du Centre de communications) ─────────
  // /admin/questions est ouvert depuis le launcher Centre de communications
  "questions": { label: "Centre de communications", href: "/admin/documents" },
};

const HIDDEN_ROOTS = new Set(["admin"]);

// Détecte un segment dynamique (UUID, MongoID, slug long)
function isDynamicSegment(seg) {
  return /^[0-9a-f-]{20,}$/i.test(seg) || (seg.length > 20 && !LABEL_MAP[seg]);
}

export function Breadcrumb({ trail }) {
  const location = useLocation();
  const { trail: contextTrail } = useBreadcrumb();
  const search = new URLSearchParams(location.search);

  // Si une page a injecté un trail via BreadcrumbContext, on l'utilise
  if (contextTrail && Array.isArray(contextTrail) && contextTrail.length > 0) {
    const items = [{ label: "Accueil", href: "/admin", isHome: true }, ...contextTrail];
    return renderBreadcrumb(items);
  }

  // Sinon : utilise le trail prop (legacy) ou auto-génère depuis l'URL
  const segments = trail || location.pathname.split("/").filter(Boolean);

  // Build clickable trail (skip /admin label, mais garde le lien racine /admin)
  const items = [{ label: "Accueil", href: "/admin", isHome: true }];
  let acc = "";
  let parentInjected = false;
  for (const seg of segments) {
    acc += "/" + seg;
    if (HIDDEN_ROOTS.has(seg)) continue;

    // Injecte le launcher parent UNE SEULE FOIS (avant le premier segment réel)
    if (!parentInjected && PARENT_LAUNCHER_MAP[seg]) {
      const parent = PARENT_LAUNCHER_MAP[seg];
      items.push({ label: parent.label, href: parent.href });
      parentInjected = true;
    }

    let label = LABEL_MAP[seg];
    if (!label) {
      label = isDynamicSegment(seg) ? `${seg.slice(0, 8)}…` : seg;
    }
    items.push({ label, href: acc });
  }

  // Ajout d'un dernier crumb basé sur ?tab=<id> (pour Settings deep-link)
  const tabParam = search.get("tab");
  if (tabParam && items.length > 1) {
    const tabLabel = QUERY_LABEL_MAP[tabParam] || LABEL_MAP[tabParam] || tabParam;
    items.push({
      label: tabLabel,
      href: `${location.pathname}?tab=${tabParam}`,
      query: true,
    });
  }

  return renderBreadcrumb(items);
}

function renderBreadcrumb(items) {
  if (items.length <= 1) return null;
  return (
    <nav
      aria-label="Fil d'Ariane"
      className="flex items-center text-xs mb-3 overflow-x-auto py-0.5"
      data-testid="breadcrumb"
    >
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        const isClickable = !!item.href && !isLast;
        return (
          <div key={`bc-${idx}-${item.label}`} className="flex items-center gap-1.5 whitespace-nowrap">
            {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" aria-hidden />}
            {!isClickable ? (
              <span
                className="text-slate-800 font-semibold inline-flex items-center gap-1"
                data-testid="breadcrumb-current"
              >
                {item.isHome && <Home className="w-3 h-3" aria-hidden />}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.href}
                className="text-slate-500 hover:text-indigo-600 hover:underline transition inline-flex items-center gap-1"
                data-testid={`breadcrumb-link-${idx}`}
              >
                {item.isHome && <Home className="w-3 h-3" aria-hidden />}
                {item.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default Breadcrumb;
